<template>
  <div class="view-container d-2304" v-if="me">
    <!-- 제목 시작 -->
    <v-layout class="mt-6">
      <v-flex>
        <p class="font-size--28 font-weight-bold mb-2" style="word-break: keep-all">
          <template v-if="me.businessSet && me.businessSet[0]">
            <span
              class="font-weight-bold color--primary-100"
              :class="`font-size--${
                $vuetify.breakpoint.xs && me.businessSet[0].name.length > 11 ? 24 : 28
              }`"
            >
              {{ me.businessSet[0].name }}
            </span>
            님의<br />
          </template>
          탄력적인 자금 운용을<br />도와드려요.
        </p>
      </v-flex>
    </v-layout>
    <!-- 제목 끝 -->

    <v-layout style="background-color: #f6f5ff" class="px-4 py-2 mb-3 round-block">
      <div style="height: 40px">
        <img src="@/assets/images/icons/exclamation-circle.svg" alt="notice" class="mx-2" />
      </div>
      <v-carousel
        cycle
        interval="3000"
        height="40"
        hide-delimiters
        hide-delimiter-background
        show-arrows-on-hover
        vertical
      >
        <template v-for="item in 6">
          <v-carousel-item class="items-center" :key="item + '-1'">
            <p class="mb-0">
              이용 <span class="primary--text font-weight-bold">고객의 83%는 베이직</span>을
              이용해요.
            </p>
            <p class="mb-0 font-size--13">(2024년 03월 31일 데이터 기준)</p>
          </v-carousel-item>

          <v-carousel-item :key="item + '-2'">
            <p class="mb-0">
              쇼핑몰 <span class="primary--text font-weight-bold">운영 상황에 따라 탄력적</span>으로
              상품을 변경하여 이용할 수 있어요.
            </p>
          </v-carousel-item>
        </template>
      </v-carousel>
    </v-layout>

    <!-- 플랜 목록 시작 -->
    <template v-if="plans">
      <section
        class="plan mb-4 round-block"
        :class="{ active: plan.isActive, basic: plan.name === '베이직' }"
        v-for="plan in plans"
        :key="plan.uuid"
      >
        <v-layout class="mb-4" align-start>
          <!-- 플랜 이름 / 설명 -->
          <v-flex class="text-block">
            <v-layout align-center>
              <span class="font-size--18 font-weight-bold">
                {{ plan.name }}
              </span>

              <!-- 설명 버튼 -->
              <v-btn
                v-if="plan.name !== '프리미엄'"
                xs
                icon
                class="btn-description"
                @click="popupDescription(plan)"
              >
                <v-icon>mdi-help-circle</v-icon>
              </v-btn>
            </v-layout>
          </v-flex>
          <!-- 플랜 신청 버튼 -->
          <v-flex class="text-right" style="max-width: 120px">
            <v-btn
              depressed
              type="button"
              min-width="100"
              :class="`${plan.isActive ? 'active' : 'white'}`"
              :disabled="
                choiceLoading ||
                meLoading ||
                getAvailableAmountLoading ||
                plan.isActive ||
                plan.isEligibleForUser !== true ||
                plan.uuid === pendingPlanUuid
              "
              :loading="choiceLoading"
              @click="choicePlan(plan)"
            >
              <!-- TODO: 프리미엄 준비중 -->
              {{
                plan.name === '프리미엄'
                  ? '준비중'
                  : plan.isActive
                  ? '사용중'
                  : plan.uuid === pendingPlanUuid
                  ? '검토 진행 중'
                  : '신청하기'
              }}
            </v-btn>
          </v-flex>
        </v-layout>
        <v-layout class="spec">
          <!-- 수수료(%) -->
          <v-flex class="text-block" style="max-width: 45%">
            <p>수수료</p>
            <p>{{ plan.feeType === 'annual_rate' ? '연' : '단건' }} {{ plan.feeRate }}%</p>
          </v-flex>
          <!-- 한도(원) -->
          <v-flex class="text-block">
            <p>한도</p>
            <p v-if="!getLimitLoading && plan.limit != null" class="amount">
              {{ commaFormat(plan.limit) }}원
            </p>
            <p v-else class="amount text-center">
              <v-progress-circular
                size="18"
                indeterminate
                :color="plan.name === '베이직' ? 'white' : 'primary'"
              />
            </p>
          </v-flex>
        </v-layout>
      </section>
    </template>
    <!-- 플랜 목록 끝 -->
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { commaFormat } from '@/util'

export default {
  data() {
    return {
      choiceLoading: false,
      getLimitLoading: false,
      planSource: null,
      dialog: false,
      changeRequestsInPending: null, // 변경 검토 요청건
    }
  },
  computed: {
    ...mapState('auth', ['me', 'meLoading']),
    ...mapState('bnpl', ['availableAmount', 'getAvailableAmountLoading']),
    plans() {
      if (!this.planSource) return null
      return this.planSource
        .filter((e) => e.isAvailable)
        .sort((a, b) => a.order - b.order)
        .map((plan) => {
          return {
            ...plan,
            color:
              plan.name === '베이직' ? '#C9974D' : plan.name === '프리미엄' ? '#6D6AFF' : '#7A7F86',
            feeRate: (plan.feeRate * 100).toFixed(1).replace('.0', ''),
            isActive: this.me.planChoice?.plan?.uuid === plan.uuid,
            limit: this.availableAmount.plans?.find((e) => e.uuid === plan.uuid).limit,
          }
        })
    },
    isShowEligibleAfterOneMonthNotice() {
      // 신규 고객은 매일정산만 사용 가능. 안내 대상 여부 boolean
      if (!this.planSource) return false
      // 일단 베이직 플랜 자격 없으면 안내 보이는 걸로
      return !this.planSource.filter((e) => e.isAvailable).find((plan) => plan.name === '베이직')
        ?.isEligibleForUser
    },

    /**
     * 검토 진행 중 플랜
     */
    pendingPlanUuid() {
      if (
        !this.me?.planChoice ||
        !this.changeRequestsInPending ||
        this.changeRequestsInPending.length < 1
      ) {
        return null
      }

      // 최근 플랜 변경된 시간
      const appliedAt = this.$moment(this.me.planChoice.appliedAt)
      // 현재 플랜 uuid
      const myPlanId = this.me.planChoice.plan.uuid

      // changeRequestsInPending(검토중인 모든 요청건)
      // 최근 플랜 변경된 시간 이후이면서
      // 요청 대상 플랜이 현재 플랜과 다른
      // 가장 최근 1건
      const pendingPlan = this.changeRequestsInPending
        .filter((e) => this.$moment(e.createdAt).isAfter(appliedAt))
        .find((e) => e.plan?.uuid !== myPlanId)

      // 요청 플랜이 사용 불가 플랜인 경우 대상에서 제외
      const plan = this.planSource.find((e) => pendingPlan?.plan?.uuid === e.uuid)
      if (!pendingPlan || !plan || !plan.isEligibleForUser) return null

      return pendingPlan.plan.uuid
    },
  },
  async created() {
    this.getLimitLoading = true
    try {
      await this.fetchData()

      await this.getAvailableAmount() // 오래 걸릴 때도 있는 api 인듯
    } finally {
      this.getLimitLoading = false
    }
  },
  methods: {
    commaFormat,
    ...mapActions('bnpl', ['getAvailableAmount']),
    ...mapActions('auth', ['getMe']),
    ...mapActions('plans', ['getPlans', 'changePlans', 'getPlansRequest']),
    async fetchData() {
      const [{ results: plans }, plansRequests, me] = await Promise.all([
        this.getPlans(),
        this.getPlansRequest({ status: 'pending' }),
        this.getMe(),
      ])

      this.planSource = plans
      // 요청 최신순 정렬
      this.changeRequestsInPending = plansRequests.sort((a, b) => (a.id - b.id) * -1)
    },

    popupDescription(plan) {
      const basic = ['매일정산 대비 최대 2배 많은 정산금', '구매확정 매출 100% 정산 + @ ']
      const maeil = ['구매확정 매출 90% 정산', '구매확정 매출 100만원 발생시 90만원']
      const source = plan.name === '베이직' ? basic : maeil
      const html = `
        <div>
          <h3 style="margin-bottom: 16px;">${plan.name}</h3>
          <ul style="list-style: none;" class="outer-ul">
            ${source
              .map((e) => `<li style="font-size: 16px !important; text-align: left;">${e}</li>`)
              .join('')} 
          </ul>
        </div>
      `

      this.$swal.basic.alert2({
        html,
      })
    },

    async choicePlan(plan) {
      if (this.choiceLoading || plan.isActive || plan.isEligibleForUser !== true) {
        return
      }

      this.choiceLoading = true
      try {
        if (this.me.planChoice) {
          const confirm = await this.$swal.basic.alert({
            html: `
              <p class="font-size--18">
                <span class="font-size--18 color--primary-100">구독모델 변경 검토</span>를 위하여<br />
                담당자(☎ <span class="font-size--18 color--primary-100">1811-2708</span>)가 전화드릴게요!
              </p>
              <br />
              <p class="color--gray-80">평일 영업일 10시~ 18시</p>
            `,
          })
          if (!confirm.isConfirmed) {
            return
          }
        }
        await this.changePlans({ plan: plan.uuid })
        await this.fetchData()
        await this.$router.push({ name: 'main' })
      } finally {
        this.choiceLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.plan {
  &.active {
    background-color: $primary-10;
  }

  p {
    margin-bottom: 0ch;
  }

  .text-block p {
    font-weight: bold !important;

    &:first-child {
      font-size: 12px !important;
      color: $gray-60;
    }

    &:last-child {
      font-size: 18px !important;
    }

    &.amount {
      color: $primary-100;
    }
  }
}

::v-deep .plan .v-btn__content {
  font-size: 16px !important;
  font-weight: bold !important;
}

.loading-img {
  width: 50%;
  display: block;
  margin: 100px auto;
}

.notice-btn {
  height: 30px !important;
}

.btn-description {
  margin-left: 5px;
  width: 24px;
  height: 24px;
  padding: 0;

  i {
    font-size: 20px !important;
  }
}

// 베이직 플랜 특수 디자인
section.basic {
  background-color: $primary-100 !important;

  p,
  span {
    color: white !important;
  }

  .spec > div > p:first-child {
    color: #ffeb3b !important;
  }

  button[disabled] {
    border: none !important;
  }
}
</style>
